import { formatInTimeZone } from "date-fns-tz";
import { HALIFAX_TZ } from "./timezone";

export interface CalendarEventInput {
  /** ISO 8601 start instant (carries its own offset). */
  start: string;
  /** ISO 8601 end instant. */
  end: string;
  title: string;
  description?: string;
  location?: string;
}

/**
 * Format an instant as a UTC iCalendar timestamp: 20260311T163000Z.
 * We anchor to UTC so the event lands at the correct wall-clock time in any
 * calendar app, regardless of the attendee's device timezone.
 */
function toUtcStamp(iso: string): string {
  return formatInTimeZone(new Date(iso), "UTC", "yyyyMMdd'T'HHmmss'Z'");
}

/** Escape per RFC 5545: commas, semicolons, backslashes and newlines. */
function escapeText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

/**
 * Build a minimal, valid VCALENDAR string for a single booking.
 * A stable UID is derived from the start time + title so re-downloading the
 * same booking updates rather than duplicates the calendar entry.
 */
export function buildIcs(event: CalendarEventInput): string {
  const uidSeed = `${toUtcStamp(event.start)}-${event.title}`
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Charme//Booking//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uidSeed}@beautybycharme.com`,
    `DTSTAMP:${toUtcStamp(event.start)}`,
    `DTSTART:${toUtcStamp(event.start)}`,
    `DTEND:${toUtcStamp(event.end)}`,
    `SUMMARY:${escapeText(event.title)}`,
    event.description ? `DESCRIPTION:${escapeText(event.description)}` : "",
    event.location ? `LOCATION:${escapeText(event.location)}` : "",
    "STATUS:CONFIRMED",
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter(Boolean);

  // RFC 5545 mandates CRLF line endings.
  return lines.join("\r\n");
}

/** Trigger a browser download of the booking as a .ics file. */
export function downloadIcs(event: CalendarEventInput, filename = "charme-booking.ics") {
  const blob = new Blob([buildIcs(event)], {
    type: "text/calendar;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Give the download a tick to start before revoking the object URL.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** A human-readable Halifax date+time range, e.g. for an .ics description. */
export function describeWhen(start: string, end: string): string {
  const date = formatInTimeZone(new Date(start), HALIFAX_TZ, "EEEE, d MMMM yyyy");
  const from = formatInTimeZone(new Date(start), HALIFAX_TZ, "h:mm a");
  const to = formatInTimeZone(new Date(end), HALIFAX_TZ, "h:mm a");
  return `${date}, ${from} – ${to} (Atlantic Time)`;
}
